import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadForm({ innerRef }) {
  const [form, setForm] = useState({ name: "", email: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.city.trim()) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/partners/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      setDone(true);
      if (data.already_registered) {
        toast.info("Vous êtes déjà inscrit — on vous recontacte très vite !");
      } else {
        toast.success("Inscription reçue ! Bienvenue chez iFotoLife.");
      }
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="rejoindre"
      ref={innerRef}
      className="py-24 md:py-32 bg-[#0A0A0A] text-white scroll-mt-20"
      data-testid="lead-form-section"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <div className="text-xs font-black uppercase tracking-label text-[#F97316] mb-5">Rejoindre le réseau</div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-[1.05]">
            Commencez à vendre <span className="text-[#F97316]">dès aujourd'hui.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/60 font-light max-w-md leading-relaxed">
            Laissez vos coordonnées : notre équipe vous contacte sous 24 h pour activer votre espace photographe.
          </p>
        </Reveal>

        <div className="lg:col-span-6 lg:col-start-8 relative">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-white text-[#0A0A0A] p-7 md:p-9"
                data-testid="lead-form"
              >
                <div className="space-y-5">
                  <div>
                    <label className="text-xs font-black uppercase tracking-label text-[#52525B]">Nom complet</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Marc Laurent"
                      className="mt-2 w-full border-b border-[#E4E4E7] bg-transparent py-2.5 text-base outline-none focus:border-[#F97316] transition-colors"
                      data-testid="lead-input-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase tracking-label text-[#52525B]">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="marc@exemple.com"
                      className="mt-2 w-full border-b border-[#E4E4E7] bg-transparent py-2.5 text-base outline-none focus:border-[#F97316] transition-colors"
                      data-testid="lead-input-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase tracking-label text-[#52525B]">Ville</label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={update("city")}
                      placeholder="Nice"
                      className="mt-2 w-full border-b border-[#E4E4E7] bg-transparent py-2.5 text-base outline-none focus:border-[#F97316] transition-colors"
                      data-testid="lead-input-city"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-8 w-full group inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-70 text-white font-semibold px-6 py-4 rounded-full transition-all duration-300"
                  data-testid="lead-submit-button"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Envoi…</>
                  ) : (
                    <>Je deviens partenaire <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" /></>
                  )}
                </button>
                <p className="mt-4 text-xs text-[#A1A1AA]">
                  En envoyant ce formulaire, vous acceptez d'être contacté par iFotoLife.
                </p>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white text-[#0A0A0A] p-9 text-center"
                data-testid="lead-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                  className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-[#F97316]/10 text-[#F97316]"
                >
                  <CheckCircle2 className="w-9 h-9" />
                </motion.div>
                <h3 className="mt-6 text-2xl font-black tracking-tight">Merci, {form.name.split(" ")[0] || "photographe"} !</h3>
                <p className="mt-3 text-sm text-[#52525B] font-light">
                  Votre demande est bien enregistrée. Notre équipe vous contacte sous 24 h à l'adresse <span className="font-medium text-[#0A0A0A]">{form.email}</span>.
                </p>
                <button
                  onClick={() => { setDone(false); setForm({ name: "", email: "", city: "" }); }}
                  className="mt-6 text-sm font-semibold text-[#F97316] hover:underline"
                  data-testid="lead-reset-button"
                >
                  Inscrire un autre photographe
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
